import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import { 
  Briefcase, Clock, Shield, Star, Phone, MapPin, 
  CheckCircle, ArrowRight, Users, Car, Building2, CalendarDays
} from 'lucide-react';

const FLEET_IMG = 'https://customer-assets.emergentagent.com/job_f40a108f-f3a3-4735-ac15-18353f3eece6/artifacts/sih2jxml_image0.jpeg';
const ESCALADE_IMG = 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/g0b5gai9_IMG_7208.jpg';
const CORPORATE_IMG = 'https://images.unsplash.com/photo-1647439258366-11bf6aca8c55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZyUyME5vcnRoZXJuJTIwVmlyZ2luaWElMjBUeXNvbnN8ZW58MHx8fHwxNzc1MTY4NjQyfDA&ixlib=rb-4.1.0&q=85';

const schema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Corporate Chauffeur Service",
  "provider": {
    "@type": "LocalBusiness",
    "name": "Capital Chauffeur Services",
    "telephone": "+1-202-684-0534",
    "email": "contact@capitalchauffeurservices.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Washington",
      "addressRegion": "DC",
      "addressCountry": "US"
    },
    "priceRange": "$$$",
    "image": FLEET_IMG
  },
  "areaServed": {
    "@type": "Place",
    "name": "Northern Virginia"
  },
  "description": "Premium corporate chauffeur service in Northern Virginia. Executive transportation for meetings, events, and daily commutes."
};

export default function CorporateNoVAPage() {
  return (
    <div data-testid="corporate-nova-page" className="min-h-screen bg-[#050505]">
      <SEOHead
        title="Corporate Chauffeur Northern Virginia | Executive Car Service | Capital Chauffeur Services"
        description="Premium corporate chauffeur service in Northern Virginia. Executive transportation for meetings, events, and daily commutes. Book your Escalade today."
        canonical="/corporate-chauffeur-northern-virginia"
        schema={schema}
      />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={ESCALADE_IMG}
            alt="Corporate chauffeur service Northern Virginia black Cadillac Escalade"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4 font-medium">Northern Virginia</p>
          <h1 data-testid="corporate-nova-h1" className="text-4xl sm:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
            Corporate Chauffeur Service<br />in Northern Virginia
          </h1>
          <p className="text-[#A3A3A3] text-lg max-w-xl mb-8 leading-relaxed">
            Dedicated executive transportation for Northern Virginia's business corridors. From Tysons Corner to Reston, Arlington to Fairfax, we move the region's professionals.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/book">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase">
                Book Corporate Service
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Why Corporate */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-3">Executive Transportation</p>
            <h2 className="text-3xl font-serif text-white mb-6">Why Northern Virginia Companies Choose Us</h2>
            <div className="space-y-4 text-[#A3A3A3] leading-relaxed">
              <p>
                Northern Virginia is home to some of the most influential companies in technology, defense, consulting, and government contracting. From the glass towers of Tysons Corner to the tech campuses along the Dulles Corridor, executives and their teams need reliable, professional transportation that matches their corporate image.
              </p>
              <p>
                Capital Chauffeur Services provides dedicated corporate chauffeur accounts for Northern Virginia businesses. Our service goes beyond simple point-to-point rides. We offer recurring daily schedules, multi-stop hourly bookings, airport transfer programs, and event transportation for corporate functions throughout the region.
              </p>
              <p>
                Every ride is in a meticulously maintained black Cadillac Escalade ESV driven by a professionally trained chauffeur. Our drivers are vetted, uniformed, and experienced with the unique demands of executive clients who expect discretion, punctuality, and flawless service.
              </p>
            </div>
          </div>
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src={CORPORATE_IMG}
              alt="Modern corporate office building in Northern Virginia Tysons Corner"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif text-white mb-4">Corporate Transportation Solutions</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: CalendarDays, title: 'Daily Executive Commute', desc: 'Recurring daily chauffeur service for C-suite executives. Consistent driver, consistent schedule, seamless commute from home to office across Northern Virginia.' },
              { icon: Briefcase, title: 'Client & VIP Transport', desc: 'Impress visiting clients, board members, and VIP guests with premium black car service. Airport pickups, hotel transfers, and meeting shuttles.' },
              { icon: Clock, title: 'Hourly Chauffeur Service', desc: 'Book your chauffeur by the hour for multi-stop meetings across NoVA. Your driver waits while you work — no rebooking, no delays between appointments.' },
              { icon: Users, title: 'Team & Group Transport', desc: 'Moving teams between offices, conference venues, and corporate events. Our Escalade ESV accommodates up to six executives comfortably.' },
              { icon: Building2, title: 'Office-to-Office Transfers', desc: 'Inter-office shuttles between your NoVA locations, DC offices, and Maryland branches. We know the routes and the traffic patterns.' },
              { icon: Shield, title: 'Dedicated Account Manager', desc: 'Corporate accounts include a dedicated contact for booking, billing, and service customization. Consolidated monthly invoicing available.' },
            ].map((service, i) => (
              <div key={i} className="border border-white/10 p-8 hover:border-[#D4AF37]/30 transition-colors">
                <service.icon className="w-8 h-8 text-[#D4AF37] mb-4" />
                <h3 className="text-white font-serif text-lg mb-3">{service.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-serif text-white mb-4 text-center">Northern Virginia Service Areas</h2>
          <p className="text-[#A3A3A3] text-center mb-10 max-w-2xl mx-auto">
            Our corporate chauffeur service covers every major business center in Northern Virginia and the surrounding DC metro area.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Tysons Corner', 'Reston', 'Arlington', 'McLean',
              'Fairfax', 'Herndon', 'Ashburn', 'Leesburg',
              'Alexandria', 'Falls Church', 'Vienna', 'Centreville',
            ].map((area, i) => (
              <div key={i} className="flex items-center gap-3 border border-white/10 p-4">
                <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                <span className="text-white text-sm">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="aspect-[21/9] overflow-hidden">
            <img
              src={FLEET_IMG}
              alt="Capital Chauffeur Services corporate fleet Northern Virginia executive car service"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-serif text-white mb-10 text-center">Corporate Account Benefits</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              'Dedicated account management with a single point of contact for all transportation needs',
              'Consolidated monthly invoicing with detailed trip logs for easy expense tracking and reporting',
              'Priority booking and guaranteed availability for recurring schedules and last-minute requests',
              'Volume discounts for companies with regular transportation needs across Northern Virginia',
              'Background-checked, professionally trained chauffeurs assigned to your account for consistency',
              'Flexible cancellation policies and 24/7 dispatch support for schedule changes',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4">
                <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <p className="text-[#A3A3A3] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-white mb-4">Explore Our Other Services</h2>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            {[
              { label: 'Dulles Airport Car Service', path: '/dulles-airport-car-service' },
              { label: 'Reagan Airport Car Service', path: '/reagan-airport-car-service' },
              { label: 'Black Car Service Arlington', path: '/black-car-service-arlington' },
              { label: 'Black Car Service Tysons', path: '/black-car-service-tysons' },
              { label: 'All Services', path: '/services' },
            ].map((link, i) => (
              <Link key={i} to={link.path}>
                <Button variant="outline" className="border-white/20 text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-none px-6 py-3 text-xs tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                  {link.label} <ArrowRight className="w-3 h-3" />
                </Button>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-white mb-4">Start Your Corporate Account</h2>
          <p className="text-[#A3A3A3] mb-8">
            Set up a corporate chauffeur account for your Northern Virginia business. Dedicated service, simplified billing, premium vehicles.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase">
                Get Started
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> Call (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
